import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { stateToHTML } from 'draft-js-export-html';

import { TextInput } from 'components/core/forms';
import Editor from 'components/shared/RichTextEditor';

import { isNullOrEmpty } from 'helpers/utils';
import { translate } from 'translations/i18n';

const FieldWrap = styled.div`
  margin-bottom: 24px;
`;

const RTEWrap = styled.div`
  padding: 12px;
  border: 1px solid var(--cc_color_border_default);
  border-radius: 4px;
  background-color: white;
`;

const EditorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  h2 {
    margin: 0;
  }

  .create-btn {
    line-height: 0;
  }
`;

const RTEError = styled.div``;

const Form = ({
  values,
  handleSubmit,
  handleChange,
  errors,
  touched,
  originalContent,
  isCreating,
  isEditing,
  isDeleting,
  handleUpdateSnippet,
  error,
  setFieldValue,
  children,
  clearSavedFlag, // For edit snippet only to determine whether or not to updateSnippet
}) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [usedSnippet, setUsedSnippet] = useState(false);

  const autoSave = () => {
    if (isCreating || isDeleting) {
      return;
    }
    handleUpdateSnippet(values);
  };

  const updateContent = (content) => {
    let entryContent = content.getPlainText();
    if (entryContent !== '') {
      entryContent = stateToHTML(content);
    }
    setFieldValue('content', entryContent);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
        setFormSubmitted(true);
        setUsedSnippet(isEditing);
      }}
    >
      {formSubmitted && !isNullOrEmpty(errors) ? (
        <div className="error-message">
          {translate('snippets.form.errorMsg')}
        </div>
      ) : null}

      <EditorHeader>{children}</EditorHeader>

      {error ? <div>{error}</div> : null}

      <div
        className={`form-field ${
          touched.title || formSubmitted ? 'error' : null
        }`}
      >
        <FieldWrap className="input-wrap">
          <TextInput
            placeholder={translate('snippets.form.title')}
            name="title"
            value={values.title}
            error={touched.title || formSubmitted ? errors.title : null}
            onChange={(e) => {
              if (clearSavedFlag) clearSavedFlag();
              handleChange(e);
            }}
            onBlur={autoSave}
            label={translate('snippets.form.title')}
            disabled={isDeleting}
            autoFocus={isCreating}
            // eslint-disable-next-line jsx-a11y/tabindex-no-positive
            tabIndex={1}
          />
        </FieldWrap>
        <RTEWrap>
          {/* usedSnippet used to de-render Editor to move focus back to report's editor */}
          {!usedSnippet ? (
            <Editor
              placeholder="Add snippet text…"
              disabled={isDeleting}
              initialContents={originalContent}
              contentChanging
              onChange={(oldState, newState) => {
                if (clearSavedFlag) clearSavedFlag();
                const newContent = newState.getCurrentContent();
                const oldContent = oldState.getCurrentContent();

                if (newContent !== oldContent) {
                  updateContent(newContent);
                }
              }}
              onBlur={(editorState) => {
                const content = editorState.getCurrentContent();
                updateContent(content);
                autoSave();
              }}
              // eslint-disable-next-line jsx-a11y/tabindex-no-positive
              tabIndex={2}
            />
          ) : null}
        </RTEWrap>
        {touched.content || formSubmitted ? (
          <RTEError>
            <div className="validation-error">{errors.content}</div>
          </RTEError>
        ) : null}
      </div>
    </form>
  );
};

Form.propTypes = {
  values: PropTypes.object,
  handleSubmit: PropTypes.func,
  handleChange: PropTypes.func,
  errors: PropTypes.object,
  touched: PropTypes.object,
  originalContent: PropTypes.string,
  isCreating: PropTypes.bool,
  isEditing: PropTypes.bool,
  isDeleting: PropTypes.bool,
  handleUpdateSnippet: PropTypes.func,
  error: PropTypes.string,
  setFieldValue: PropTypes.func,
  children: PropTypes.node,
  clearSavedFlag: PropTypes.func,
};

export default Form;
